import styled from "@emotion/styled";
import { Flex } from "@radix-ui/themes";
import { observer } from "mobx-react-lite";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { theme } from "@themes/themes";

import { BN } from "@utils/BN";

import { Accordion, Separator } from "@src/ui-kit";

import { OrderInput } from "./OrderInput";

interface Props {
  entryPrice: BN;
  amount: BN;
  side: "buy" | "sell";
  takeProfitPrice: BN;
  gainPercent: BN;
  stopLossPrice: BN;
  lossPercent: BN;
  decimalScale?: number;
  displayDecimals?: number;
  onTakeProfitPriceChange: (v: BN) => void;
  onGainPercentChange: (v: BN) => void;
  onStopLossPriceChange: (v: BN) => void;
  onLossPercentChange: (v: BN) => void;
}

const isZero = (val: BN) => val.isZero() || val.isNaN();

export const TakeProfitStopLossAccordion: React.FC<Props> = observer(
  ({
    entryPrice,
    amount,
    side,
    takeProfitPrice,
    gainPercent,
    stopLossPrice,
    lossPercent,
    displayDecimals,
    onTakeProfitPriceChange,
    onGainPercentChange,
    onStopLossPriceChange,
    onLossPercentChange,
  }) => {
    const { t } = useTranslation();
    const [lastEdited, setLastEdited] = useState<"tp" | "gain" | "sl" | "loss" | null>(null);

    const tpError =
      !isZero(takeProfitPrice) && (side === "buy" ? takeProfitPrice.lte(entryPrice) : takeProfitPrice.gte(entryPrice));
    const slError =
      !isZero(stopLossPrice) && (side === "buy" ? stopLossPrice.gte(entryPrice) : stopLossPrice.lte(entryPrice));

    const isTpActive =
      side === "buy"
        ? takeProfitPrice.gt(entryPrice) && !isZero(takeProfitPrice)
        : takeProfitPrice.lt(entryPrice) && !isZero(takeProfitPrice);
    const isSlActive =
      side === "buy"
        ? stopLossPrice.lt(entryPrice) && !isZero(stopLossPrice)
        : stopLossPrice.gt(entryPrice) && !isZero(stopLossPrice);

    useEffect(() => {
      if (lastEdited === "tp") {
        const gain =
          side === "buy"
            ? takeProfitPrice.minus(entryPrice).div(entryPrice).multipliedBy(100)
            : entryPrice.minus(takeProfitPrice).div(entryPrice).multipliedBy(100);
        onGainPercentChange(gain);
      }

      if (lastEdited === "gain") {
        const tp =
          side === "buy"
            ? entryPrice.multipliedBy(gainPercent.div(100).plus(1))
            : entryPrice.multipliedBy(new BN(1).minus(gainPercent.div(100)));
        onTakeProfitPriceChange(tp);
      }

      setLastEdited(null);
    }, [
      takeProfitPrice,
      gainPercent,
      entryPrice,
      amount,
      lastEdited,
      onGainPercentChange,
      onTakeProfitPriceChange,
      side,
    ]);

    useEffect(() => {
      if (lastEdited === "sl") {
        const loss =
          side === "buy"
            ? entryPrice.minus(stopLossPrice).div(entryPrice).multipliedBy(100)
            : stopLossPrice.minus(entryPrice).div(entryPrice).multipliedBy(100);
        onLossPercentChange(loss);
      }

      if (lastEdited === "loss") {
        const sl =
          side === "buy"
            ? entryPrice.multipliedBy(new BN(1).minus(lossPercent.div(100)))
            : entryPrice.multipliedBy(lossPercent.div(100).plus(1));
        onStopLossPriceChange(sl);
      }

      setLastEdited(null);
    }, [stopLossPrice, lossPercent, entryPrice, amount, lastEdited, onLossPercentChange, onStopLossPriceChange, side]);

    const handleChange = (setter: (v: BN) => void, field: typeof lastEdited) => (val: BN) => {
      if (val.isZero()) {
        if (field === "tp" || field === "gain") {
          onTakeProfitPriceChange(BN.ZERO);
          onGainPercentChange(BN.ZERO);
        }
        if (field === "sl" || field === "loss") {
          onStopLossPriceChange(BN.ZERO);
          onLossPercentChange(BN.ZERO);
        }

        return;
      }

      setLastEdited(field);
      setter(new BN(val ?? BN.ZERO));
    };

    const estimatedProfit =
      isTpActive && !isZero(entryPrice)
        ? (side === "buy" ? takeProfitPrice.minus(entryPrice) : entryPrice.minus(takeProfitPrice)).multipliedBy(amount)
        : BN.ZERO;

    const estimatedLoss =
      isSlActive && !isZero(entryPrice)
        ? (side === "buy" ? entryPrice.minus(stopLossPrice) : stopLossPrice.minus(entryPrice)).multipliedBy(amount)
        : BN.ZERO;

    const formatPrice = (price: BN) => {
      return Number(BN.formatUnits(price, 0).toString()).toFixed(displayDecimals ?? 2);
    };

    const valueLabel = (
      <Flex align="center" gap="4px">
        {isTpActive && <ProfitText isProfit>{formatPrice(takeProfitPrice)}</ProfitText>}
        {isSlActive && <ProfitText isProfit={false}>{formatPrice(stopLossPrice)}</ProfitText>}
      </Flex>
    );

    return (
      <Accordion
        triggerTestId="tradebox:tpsl:trigger"
        contentTestId="tradebox:tpsl:content"
        label={t("tpsl.title")}
        value={valueLabel}
      >
        <Flex direction="column" gap="8px">
          <Separator color="accent-7" size="4" />
          <Flex gap="8px" width="100%">
            <OrderInput
              decimalScale={0}
              displayDecimals={displayDecimals}
              error={tpError}
              label={t("tpsl.takeProfitPrice")}
              placeholder="0.00"
              value={takeProfitPrice}
              variant="secondary"
              onChange={handleChange(onTakeProfitPriceChange, "tp")}
              inputTestId="tradebox:tpsl:tp-price"
            />
            <OrderInput
              inputTestId="tradebox:tpsl:tp-percent"
              decimalScale={0}
              displayDecimals={2}
              error={tpError}
              label={t("common.gain")}
              placeholder="0.00"
              rightLabel="%"
              value={gainPercent}
              variant="secondary"
              onChange={handleChange(onGainPercentChange, "gain")}
            />
          </Flex>
          <Flex justify="between">
            <RowText>Estimated Profit</RowText>
            <ProfitText isProfit={isTpActive ? true : undefined}>
              {isTpActive ? `+$${estimatedProfit.toFormat(2)}` : "-"}
            </ProfitText>
          </Flex>
          <Flex gap="8px" width="100%">
            <OrderInput
              inputTestId="tradebox:tpsl:sl-price"
              decimalScale={0}
              displayDecimals={displayDecimals}
              error={slError}
              label={t("tpsl.stopLossPrice")}
              placeholder="0.00"
              value={stopLossPrice}
              variant="secondary"
              onChange={handleChange(onStopLossPriceChange, "sl")}
            />
            <OrderInput
              inputTestId="tradebox:tpsl:sl-percent"
              decimalScale={0}
              displayDecimals={2}
              error={slError}
              label={t("common.loss")}
              placeholder="0.00"
              rightLabel="%"
              value={lossPercent}
              variant="secondary"
              onChange={handleChange(onLossPercentChange, "loss")}
            />
          </Flex>
          <Flex justify="between">
            <RowText>Estimated Loss</RowText>
            <ProfitText isProfit={isSlActive ? false : undefined}>
              {isSlActive ? `-$${estimatedLoss.toFormat(2)}` : "-"}
            </ProfitText>
          </Flex>
        </Flex>
      </Accordion>
    );
  },
);

const RowText = styled.span`
  ${theme.font.body12};
  color: ${theme.color("gray-11")};
`;

const ProfitText = styled.span<{ isProfit?: boolean }>`
  ${theme.font.body12};
  color: ${({ isProfit }) =>
    isProfit === true
      ? theme.color("green-strong")
      : isProfit === false
        ? theme.color("red-strong")
        : theme.color("accent-contrast")};
`;
